/**
 * Cotações de ações e FII via brapi.dev.
 *
 * O token é embutido no bundle (toda env `VITE_` é pública). É um token
 * gratuito e somente-leitura, mas se precisar esconder, a saída é um proxy
 * (Cloud Function) em vez de chamar a API direto do navegador.
 */

const BRAPI_URL = 'https://brapi.dev/api/quote';
const TOKEN = import.meta.env.VITE_BRAPI_TOKEN as string | undefined;

/**
 * Ativos por requisição. O plano gratuito da brapi aceita só 1; os pagos
 * aceitam mais. Dá para aumentar por env se você mudar de plano.
 */
const BATCH_SIZE = Math.max(
  1,
  Number(import.meta.env.VITE_BRAPI_BATCH_SIZE ?? 1) || 1
);

/**
 * Requisições simultâneas. O plano gratuito aceita 1 (ela anuncia isso no
 * cabeçalho `x-brapi-concurrency-limit`), e a segunda chamada em paralelo
 * volta 429. Começamos em 1 e ajustamos se a API disser que cabe mais.
 */
const INITIAL_CONCURRENCY = Math.max(
  1,
  Number(import.meta.env.VITE_BRAPI_CONCURRENCY ?? 1) || 1
);

/** Uma recusa por concorrência é transitória: espera e tenta de novo. */
const MAX_RETRIES = 3;
const RETRY_DELAYS_MS = [400, 1200, 2500];

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export interface Quote {
  ticker: string;
  price: number;
  changePercent?: number;
  name?: string;
}

export class QuotesError extends Error {}

/** 429 — pode valer a pena tentar de novo. */
class RetryableQuotesError extends QuotesError {
  constructor(
    message: string,
    readonly transient: boolean,
    readonly retryAfterSeconds: number
  ) {
    super(message);
  }
}

/** Ajustado pelo cabeçalho da primeira resposta. */
let concurrencyLimit = INITIAL_CONCURRENCY;

interface BrapiResult {
  symbol?: string;
  regularMarketPrice?: number;
  regularMarketChangePercent?: number;
  longName?: string;
  shortName?: string;
}

function chunk<T>(items: T[], size: number): T[][] {
  const batches: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    batches.push(items.slice(i, i + size));
  }
  return batches;
}

async function fetchBatch(tickers: string[]): Promise<Quote[]> {
  const params = new URLSearchParams();
  if (TOKEN) params.set('token', TOKEN);
  const url = `${BRAPI_URL}/${tickers.join(',')}?${params.toString()}`;

  const response = await fetch(url);

  // A brapi anuncia quantas chamadas simultâneas o plano aceita.
  const announced = Number(response.headers.get('x-brapi-concurrency-limit') ?? 0);
  if (announced > 0) concurrencyLimit = announced;

  // Ela explica o motivo no corpo (limite do plano, token inválido, cota
  // estourada). Repassar a mensagem dela vale mais que o código HTTP.
  const data = (await response.json().catch(() => null)) as {
    results?: BrapiResult[];
    error?: boolean | string;
    message?: string;
    code?: string;
  } | null;

  if (response.status === 429) {
    // Concorrência é transitória e vale retry; cota por minuto, não.
    const isConcurrency =
      /simultane|concurren/i.test(data?.message ?? '') ||
      Number(response.headers.get('ratelimit-remaining') ?? 1) > 0;
    throw new RetryableQuotesError(
      data?.message ??
        'Limite de requisições da brapi atingido. Tente de novo em um minuto.',
      isConcurrency,
      Number(response.headers.get('retry-after') ?? 0)
    );
  }

  if (!response.ok || data?.error) {
    const fromApi = data?.message ?? (typeof data?.error === 'string' ? data.error : null);
    if (fromApi) throw new QuotesError(fromApi);
    throw new QuotesError(
      response.status === 401 || response.status === 403
        ? 'Token da brapi inválido ou expirado.'
        : `Falha ao buscar cotações (HTTP ${response.status}).`
    );
  }
  if (!data) throw new QuotesError('Resposta inesperada da brapi.');

  return (data.results ?? []).flatMap((result) => {
    if (!result.symbol || typeof result.regularMarketPrice !== 'number') return [];
    return [
      {
        ticker: result.symbol.toUpperCase(),
        price: result.regularMarketPrice,
        changePercent: result.regularMarketChangePercent,
        name: result.longName ?? result.shortName,
      },
    ];
  });
}

/** Repete a chamada enquanto a recusa for por concorrência. */
async function fetchWithRetry(tickers: string[]): Promise<Quote[]> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fetchBatch(tickers);
    } catch (error) {
      const retryable =
        error instanceof RetryableQuotesError && error.transient && attempt < MAX_RETRIES;
      if (!retryable) throw error;

      const suggested = (error as RetryableQuotesError).retryAfterSeconds * 1000;
      await sleep(Math.max(suggested, RETRY_DELAYS_MS[attempt] ?? 2500));
    }
  }
}

/** Busca as cotações em lotes. Lança QuotesError se nenhuma requisição funcionar. */
export async function fetchQuotes(tickers: string[]): Promise<Map<string, Quote>> {
  const unique = [...new Set(tickers.map((t) => t.trim().toUpperCase()).filter(Boolean))];
  if (unique.length === 0) return new Map();

  const batches = chunk(unique, BATCH_SIZE);
  const quotes = new Map<string, Quote>();
  const failures: unknown[] = [];

  let next = 0;
  const workers = Array.from(
    { length: Math.min(concurrencyLimit, batches.length) },
    async () => {
      while (next < batches.length) {
        const batch = batches[next++]!;
        try {
          for (const quote of await fetchWithRetry(batch)) quotes.set(quote.ticker, quote);
        } catch (error) {
          failures.push(error);
        }
      }
    }
  );
  await Promise.all(workers);

  if (quotes.size === 0 && failures.length > 0) {
    const first = failures[0];
    throw first instanceof Error ? first : new QuotesError('Falha ao buscar cotações.');
  }

  return quotes;
}

/** Busca um ticker — usado para autocompletar nome e preço no cadastro. */
export async function fetchQuote(ticker: string): Promise<Quote | null> {
  const quotes = await fetchQuotes([ticker]);
  return quotes.get(ticker.trim().toUpperCase()) ?? null;
}

export const quotesConfigured = Boolean(TOKEN);
